import { useState, useEffect, useCallback } from "react";
import { useOutletContext, useNavigate } from "react-router-dom";
import api, { formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter, DialogDescription,
} from "@/components/ui/dialog";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import {
  Server, Plus, Loader2, Building2, Landmark, ShieldCheck, RefreshCw, Wifi, WifiOff, Search,
  Lock, CheckCircle2, Star, ShieldAlert,
} from "lucide-react";

const BROKERS = [
  "AAAFx Global Ltd", "Exness", "IC Markets", "XM", "Pepperstone", "FBS", "OctaFX", "Deriv",
  "RoboForex", "HFM (HotForex)", "FxPro", "Admiral Markets", "FXTM (ForexTime)",
  "Tickmill", "Vantage", "Eightcap", "BlackBull Markets", "Fusion Markets",
  "ThinkMarkets", "AvaTrade", "OANDA", "FOREX.com", "Alpari", "JustMarkets",
  "Axi", "Global Prime", "Traders Trust", "FXOpen", "Errante", "Fondex",
  "Purple Trading", "Windsor Brokers", "Libertex", "InstaForex", "Weltrade",
  "NordFX", "AMarkets", "SquaredFinancial", "CMC Markets", "Swissquote",
  "Dukascopy", "Interactive Brokers", "Saxo Bank", "Razor Markets", "Doo Prime",
  "Zero Markets", "Valutrades", "Skilling", "Scope Markets", "Hantec Markets",
  "IG", "FXCM", "Blueberry Markets", "Moneta Markets", "GO Markets", "TMGM",
  "VT Markets", "Trade Nation", "ACY Securities", "StarTrader", "TigerWit",
  "easyMarkets", "Capital.com", "MultiBank", "Equiti", "ATFX", "Grand Capital",
  "LiteFinance", "FIBO Group", "FreshForex", "ForexMart", "Tifia", "FXChoice",
  "Coinexx", "Hugo's Way", "OspreyFX", "IFC Markets", "Turnkey Forex", "PU Prime",
  "IUX Markets", "Headway", "SuperForex", "Land Prime", "Darwinex", "MEX Atlantic",
  "FXGiants", "Sway Markets", "GMI Markets", "N1CM", "Potrade Ltd", "AAA Fx",
];

const PROP_FIRMS = [
  "E8 Markets", "FTMO", "The5ers", "FundedNext", "Funding Pips", "FXIFY",
  "FunderPro", "Alpha Capital Group", "Goat Funded Trader", "Maven Trading",
  "Topstep", "Apex Trader Funding", "Other Prop Firm",
];

const PHASES = [
  { id: "evaluation", label: "Evaluation" },
  { id: "verification", label: "Verification" },
  { id: "funded", label: "Funded Account" },
];

function Combobox({ options, value, onChange, placeholder, icon: Icon = Building2, inputTestId, listTestId, optionPrefix }) {
  const [openList, setOpenList] = useState(false);
  const q = (value || "").trim().toLowerCase();
  const matches = (q ? options.filter((b) => b.toLowerCase().includes(q)) : options).slice(0, 8);
  const show = openList && matches.length > 0;
  return (
    <div className="relative">
      <div className="relative">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-[#5f7ba6] pointer-events-none" />
        <Input
          data-testid={inputTestId}
          value={value}
          onChange={(e) => { onChange(e.target.value); setOpenList(true); }}
          onFocus={() => setOpenList(true)}
          onBlur={() => setTimeout(() => setOpenList(false), 150)}
          placeholder={placeholder}
          autoComplete="off"
          className="mt-1 pl-9 bg-[#060c18] border-[#14233d] rounded-xl"
        />
      </div>
      {show && (
        <div data-testid={listTestId}
          className="absolute z-50 left-0 right-0 mt-1 max-h-56 overflow-y-auto rounded-xl border border-[#14233d] bg-[#0A1120] shadow-2xl">
          {matches.map((b) => (
            <button key={b} type="button"
              data-testid={`${optionPrefix}-${b.toLowerCase().replace(/[^a-z0-9]+/g, "-").replace(/^-|-$/g, "")}`}
              onMouseDown={(e) => { e.preventDefault(); onChange(b); setOpenList(false); }}
              className="w-full text-left px-4 py-2.5 text-sm text-[#cfe0f5] hover:bg-[#0f1a2e] hover:text-white flex items-center gap-2 transition-colors">
              <Icon className="h-3.5 w-3.5 text-[#5f7ba6] shrink-0" /> {b}
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

function LiveMetric({ label, value, tone }) {
  const color = tone === "up" ? "text-[#22e07b]" : tone === "down" ? "text-[#ff5b5b]" : "text-white";
  return (
    <div className="bg-[#0A1120] px-5 py-4">
      <div className="font-mono-term text-[10px] uppercase tracking-widest text-[#5f7ba6] mb-1">{label}</div>
      <div className={`font-heading text-lg font-bold ${color}`}>{value}</div>
    </div>
  );
}

const fmtMoney = (v, ccy) => (v == null ? "—" :
  `${ccy === "USD" || !ccy ? "$" : ""}${Number(v).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}${ccy && ccy !== "USD" ? " " + ccy : ""}`);

function ObjectiveRow({ label, hint, obj, onChange, testid }) {
  return (
    <div>
      <Label className="text-xs text-[#5f7ba6]">{label}</Label>
      <div className="mt-1 flex gap-2">
        <Input type="number" value={obj.value} data-testid={testid}
          onChange={(e) => onChange({ ...obj, value: e.target.value })}
          placeholder={hint} className="bg-[#060c18] border-[#14233d] rounded-xl h-11 flex-1" />
        <div className="flex rounded-xl border border-[#14233d] overflow-hidden shrink-0">
          {["amount", "percent"].map((t) => (
            <button key={t} type="button" data-testid={`${testid}-${t}`}
              onClick={() => onChange({ ...obj, type: t })}
              className={`px-3.5 text-sm font-bold transition-colors ${obj.type === t ? "text-white" : "text-[#5f7ba6] hover:text-white"}`}
              style={obj.type === t ? { backgroundColor: "var(--sfx-accent)" } : undefined}>
              {t === "amount" ? "$" : "%"}
            </button>
          ))}
        </div>
      </div>
    </div>
  );
}

function ConnectDialog({ open, setOpen, tab, setTab, mt5, setMt5, prop, setProp, busy, save, trigger, propfirmLocked, phases }) {
  const phaseCfg = phases?.[prop.challenge_phase];
  const setObj = (key, val) => setProp({ ...prop, objectives: { ...prop.objectives, [key]: val } });
  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>{trigger}</DialogTrigger>
      <DialogContent className="bg-[#0A1120] border-[#14233d] text-white p-0 gap-0 w-screen h-[100dvh] max-w-full sm:max-w-[440px] sm:w-full sm:h-auto sm:max-h-[92vh] rounded-none sm:rounded-2xl overflow-hidden flex flex-col">
        <DialogHeader className="shrink-0 px-5 pt-5 pb-3 border-b border-[#14233d]">
          <DialogTitle className="font-heading text-lg">Connect MT5 Account</DialogTitle>
          <DialogDescription className="text-[#5f7ba6] text-sm">Enter your broker credentials to connect securely via MetaApi.</DialogDescription>
        </DialogHeader>
        <div className="flex-1 overflow-y-auto px-5 py-4">
        <Tabs value={tab} onValueChange={setTab}>
          <TabsList className="grid grid-cols-2 bg-[#060c18] rounded-xl h-11">
            <TabsTrigger value="mt5" data-testid="tab-mt5" className="rounded-lg data-[state=active]:text-white" style={tab === "mt5" ? { backgroundColor: "var(--sfx-accent)" } : undefined}>MT5 Broker</TabsTrigger>
            <TabsTrigger value="propfirm" data-testid="tab-propfirm" className="rounded-lg data-[state=active]:text-white" style={tab === "propfirm" ? { backgroundColor: "var(--sfx-accent)" } : undefined}>Prop Firm</TabsTrigger>
          </TabsList>
          <TabsContent value="mt5" className="space-y-4 mt-4">
            <div><Label className="text-xs text-[#5f7ba6]">Broker (optional)</Label><Combobox options={BROKERS} value={mt5.broker} onChange={(v) => setMt5({ ...mt5, broker: v })} placeholder="Type your broker, e.g. Exness" inputTestId="mt5-broker" listTestId="broker-suggestions" optionPrefix="broker-option" /></div>
            <div><Label className="text-xs text-[#5f7ba6]">MT5 Login</Label><Input data-testid="mt5-account-id" value={mt5.account_id} onChange={(e) => setMt5({ ...mt5, account_id: e.target.value })} placeholder="4025163" className="mt-1 bg-[#060c18] border-[#14233d] rounded-xl h-11" /></div>
            <div><Label className="text-xs text-[#5f7ba6]">Password (master — required to trade)</Label><Input data-testid="mt5-password" type="password" value={mt5.password} onChange={(e) => setMt5({ ...mt5, password: e.target.value })} placeholder="••••••••" className="mt-1 bg-[#060c18] border-[#14233d] rounded-xl h-11" /></div>
            <div><Label className="text-xs text-[#5f7ba6]">Broker Server</Label><Input data-testid="mt5-server" value={mt5.server} onChange={(e) => setMt5({ ...mt5, server: e.target.value })} placeholder="RazorMarkets-Live" className="mt-1 bg-[#060c18] border-[#14233d] rounded-xl h-11" /></div>
            <div className="flex items-start gap-2 text-xs text-[#5f7ba6] pt-1"><ShieldCheck className="h-4 w-4 shrink-0 mt-0.5" style={{ color: "var(--sfx-accent)" }} /> Use your MASTER password (not investor / read-only) so the Smart Scalper can place trades.</div>
          </TabsContent>
          <TabsContent value="propfirm" className="space-y-4 mt-4">
            {propfirmLocked ? (
              <div className="border border-[#14233d] bg-[#060c18] rounded-2xl p-6 text-center" data-testid="propfirm-locked">
                <div className="h-12 w-12 rounded-full mx-auto mb-3 flex items-center justify-center" style={{ backgroundColor: "var(--sfx-accent-soft)" }}>
                  <Lock className="h-5 w-5" style={{ color: "var(--sfx-accent)" }} />
                </div>
                <div className="font-heading font-semibold mb-1">Prop Firm Mode locked</div>
                <p className="text-xs text-[#5f7ba6]">Upgrade to Pro to unlock Prop Firm Mode with automated risk protection.</p>
              </div>
            ) : (
              <>
                <div><Label className="text-xs text-[#5f7ba6]">Prop Firm</Label><Combobox options={PROP_FIRMS} value={prop.firm_name} onChange={(v) => setProp({ ...prop, firm_name: v })} placeholder="Type your prop firm, e.g. FTMO" icon={Landmark} inputTestId="prop-firm-name" listTestId="propfirm-suggestions" optionPrefix="propfirm-option" /></div>
                <div>
                  <Label className="text-xs text-[#5f7ba6]">Account Phase</Label>
                  <div className="grid grid-cols-3 gap-2 mt-1">
                    {PHASES.map((ph) => {
                      const on = prop.challenge_phase === ph.id;
                      return (
                        <button key={ph.id} type="button" data-testid={`prop-phase-${ph.id}`}
                          onClick={() => setProp({ ...prop, challenge_phase: ph.id })}
                          className={`px-2 py-2.5 rounded-xl border text-xs font-semibold transition-colors ${on ? "text-white" : "border-[#14233d] text-[#7f9bc4] hover:border-[#24406b]"}`}
                          style={on ? { borderColor: "var(--sfx-accent)", backgroundColor: "var(--sfx-accent-soft)" } : undefined}>
                          {ph.label}
                        </button>
                      );
                    })}
                  </div>
                </div>
                <div><Label className="text-xs text-[#5f7ba6]">Account Balance ($)</Label><Input data-testid="prop-balance" type="number" value={prop.balance} onChange={(e) => setProp({ ...prop, balance: e.target.value })} placeholder="100000" className="mt-1 bg-[#060c18] border-[#14233d] rounded-xl h-11" /></div>

                <div className="border border-[#14233d] bg-[#060c18] rounded-2xl p-3.5 space-y-3.5" data-testid="prop-objectives">
                  <div className="flex items-center gap-1.5 text-[#8fb4e6] font-semibold text-xs"><ShieldAlert className="h-3.5 w-3.5" /> Trading Objectives (your prop firm's rules)</div>
                  <p className="text-[11px] text-[#5f7ba6] -mt-1">Enter the exact limits from your prop firm. Toggle $ or %. The Smart Scalper enforces these before every trade.</p>
                  <ObjectiveRow label="Max Daily Loss" hint="e.g. 5 (%) or 300 ($)" obj={prop.objectives.daily_loss} onChange={(v) => setObj("daily_loss", v)} testid="obj-daily-loss" />
                  <ObjectiveRow label="Max Loss / Maximum Drawdown" hint="e.g. 10 (%) or 1000 ($)" obj={prop.objectives.max_drawdown} onChange={(v) => setObj("max_drawdown", v)} testid="obj-max-drawdown" />
                  <ObjectiveRow label="EOD Trailing Drawdown (if applicable)" hint="optional" obj={prop.objectives.trailing_drawdown} onChange={(v) => setObj("trailing_drawdown", v)} testid="obj-trailing" />
                  <ObjectiveRow label="Profit Target" hint="e.g. 8 (%) or 1000 ($)" obj={prop.objectives.profit_target} onChange={(v) => setObj("profit_target", v)} testid="obj-profit-target" />
                  <p className="text-[11px] text-[#5f7ba6] pt-1 border-t border-[#14233d]">Leave blank to use the {phaseCfg?.label || "phase"} defaults · Max Lot {phaseCfg?.max_lot} · Max Open {phaseCfg?.max_open_trades} · Risk/Trade {phaseCfg?.max_risk_per_trade_pct}%</p>
                </div>
                <div><Label className="text-xs text-[#5f7ba6]">MT5 Login</Label><Input data-testid="prop-account-id" value={prop.account_id} onChange={(e) => setProp({ ...prop, account_id: e.target.value })} placeholder="510023987" className="mt-1 bg-[#060c18] border-[#14233d] rounded-xl h-11" /></div>
                <div><Label className="text-xs text-[#5f7ba6]">Password (master)</Label><Input data-testid="prop-password" type="password" value={prop.password} onChange={(e) => setProp({ ...prop, password: e.target.value })} placeholder="••••••••" className="mt-1 bg-[#060c18] border-[#14233d] rounded-xl h-11" /></div>
                <div><Label className="text-xs text-[#5f7ba6]">Broker Server</Label><Input data-testid="prop-server" value={prop.server} onChange={(e) => setProp({ ...prop, server: e.target.value })} placeholder="FTMO-Server3" className="mt-1 bg-[#060c18] border-[#14233d] rounded-xl h-11" /></div>
              </>
            )}
          </TabsContent>
        </Tabs>
        </div>
        <DialogFooter className="shrink-0 px-5 py-4 border-t border-[#14233d]">
          <Button onClick={save} data-testid="save-account-button" disabled={busy || (tab === "propfirm" && propfirmLocked)}
            className="h-12 rounded-full text-white font-semibold w-full hover:opacity-90" style={{ backgroundColor: "var(--sfx-accent)" }}>
            {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : "Connect Account"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function AccountCard({ acc, isActive, onActivate, onRemove, onSync, busyId, canRemove }) {
  const isProp = acc.type === "propfirm";
  const connected = !!(acc.metaapi_account_id && (acc.metaapi_status === "linked" || acc.status === "connected"));
  return (
    <div className={`border rounded-3xl overflow-hidden ${isActive ? "" : "border-[#14233d]"}`}
      style={isActive ? { borderColor: "var(--sfx-accent)" } : undefined}
      data-testid={`account-card-${acc.id}`}>
      <div className="flex items-center justify-between gap-3 px-5 py-4 bg-[#0A1120]">
        <div className="flex items-center gap-3 min-w-0">
          <div className="h-10 w-10 rounded-xl flex items-center justify-center shrink-0" style={{ backgroundColor: "var(--sfx-accent-soft)" }}>
            {isProp ? <Landmark className="h-5 w-5" style={{ color: "var(--sfx-accent)" }} /> : <Building2 className="h-5 w-5" style={{ color: "var(--sfx-accent)" }} />}
          </div>
          <div className="min-w-0">
            <div className="font-heading font-semibold truncate flex items-center gap-1.5">
              {isProp ? acc.firm_name : acc.broker}
              {isActive && <span className="inline-flex items-center gap-1 text-[10px] font-bold uppercase tracking-wider px-2 py-0.5 rounded-full text-white" style={{ backgroundColor: "var(--sfx-accent)" }} data-testid="active-badge"><Star className="h-2.5 w-2.5" /> Active</span>}
            </div>
            <div className="font-mono-term text-[11px] uppercase tracking-widest flex items-center gap-1.5" data-testid={`account-status-${acc.id}`}>
              {connected
                ? <><Wifi className="h-3 w-3 text-[#22e07b]" /><span className="text-[#22e07b]">Connected</span></>
                : <><WifiOff className="h-3 w-3 text-[#f5a623]" /><span className="text-[#f5a623]">Disconnected</span></>}
            </div>
          </div>
        </div>
        <div className="flex items-center gap-1.5 shrink-0">
          {!isActive && (
            <Button onClick={() => onActivate(acc.id)} data-testid={`activate-account-${acc.id}`} disabled={busyId === acc.id}
              variant="outline" size="sm" className="h-8 rounded-full border-[#24406b] bg-transparent text-white hover:bg-[#0f1a2e] text-xs px-3">
              {busyId === acc.id ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : "Activate Account"}
            </Button>
          )}
          {isActive && (
            <Button onClick={() => onSync(acc.id)} data-testid="account-sync-button" size="sm" disabled={busyId === acc.id}
              variant="outline" className="h-8 w-8 p-0 rounded-full border-[#24406b] bg-transparent text-white hover:bg-[#0f1a2e]" title="Refresh connection">
              <RefreshCw className={`h-3.5 w-3.5 ${busyId === acc.id ? "animate-spin" : ""}`} />
            </Button>
          )}
          {canRemove && (
            <Button onClick={() => onRemove(acc.id)} data-testid={`remove-account-${acc.id}`}
              size="sm" variant="outline" disabled={busyId === acc.id}
              className="h-8 rounded-full border-[#3a1f2a] bg-transparent text-[#ff5b5b] hover:bg-[#1f0f14] text-xs px-3 gap-1.5">
              {busyId === acc.id ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <><WifiOff className="h-3.5 w-3.5" /> Dekonekte</>}
            </Button>
          )}
        </div>
      </div>
      <div className="grid grid-cols-2 gap-px bg-[#14233d] text-sm">
        <div className="bg-[#0A1120] px-5 py-3"><div className="font-mono-term text-[10px] uppercase tracking-widest text-[#5f7ba6]">Account ID</div><div className="font-heading font-semibold">{acc.account_id}</div></div>
        <div className="bg-[#0A1120] px-5 py-3"><div className="font-mono-term text-[10px] uppercase tracking-widest text-[#5f7ba6]">Server</div><div className="font-heading font-semibold truncate">{acc.server}</div></div>
        <div className="bg-[#0A1120] px-5 py-3"><div className="font-mono-term text-[10px] uppercase tracking-widest text-[#5f7ba6]">Type</div><div className="font-heading font-semibold">{isProp ? `Prop Firm · ${acc.challenge_phase || "Evaluation"}` : "MT5 Broker"}</div></div>
        <div className="bg-[#0A1120] px-5 py-3"><div className="font-mono-term text-[10px] uppercase tracking-widest text-[#5f7ba6] flex items-center gap-1">Balance {acc.live && <span className="text-[8px] font-bold px-1 py-px rounded bg-[#22e07b]/15 text-[#22e07b]" data-testid={`balance-live-${acc.id}`}>LIVE</span>}</div>
          {isProp || acc.live
            ? <div className="font-heading font-semibold" data-testid={`account-balance-${acc.id}`}>{fmtMoney(acc.balance, acc.currency)}</div>
            : <div data-testid={`account-balance-${acc.id}`}><div className="font-heading font-semibold text-[#5f7ba6]">—</div><div className="text-[9px] text-[#f5a623] leading-tight mt-0.5">Live balance unavailable{connected ? " · top up MetaAPI" : ""}</div></div>}
        </div>
      </div>
    </div>
  );
}

export default function Account() {
  const { accounts, refreshAll, entitlements } = useOutletContext();
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  const [busyId, setBusyId] = useState(null);
  const [tab, setTab] = useState("mt5");
  const [mt5, setMt5] = useState({ broker: "", account_id: "", password: "", server: "" });
  const [prop, setProp] = useState({ firm_name: "", challenge_phase: "evaluation", account_id: "", password: "", server: "", balance: "100000",
    objectives: {
      daily_loss: { value: "", type: "percent" },
      max_drawdown: { value: "", type: "percent" },
      trailing_drawdown: { value: "", type: "amount" },
      profit_target: { value: "", type: "percent" },
    } });
  const [live, setLive] = useState(null);
  const [syncing, setSyncing] = useState(false);

  const list = accounts || [];
  const activeAcc = list.find((a) => a.active) || list[0];
  const maxAccounts = entitlements?.max_accounts ?? 1;
  const propfirmLocked = !entitlements?.propfirm;
  const canAddMore = entitlements?.active && list.length < maxAccounts;

  const fetchLive = useCallback(async () => {
    if (!activeAcc) return;
    setSyncing(true);
    try {
      const { data } = await api.get("/account/live");
      setLive(data);
    } catch { /* ignore */ } finally { setSyncing(false); }
  }, [activeAcc?.id]);

  useEffect(() => { fetchLive(); }, [fetchLive]);

  const activate = async (id) => {
    setBusyId(id);
    try {
      await api.post(`/accounts/${id}/activate`);
      toast.success("Active account switched");
      await refreshAll();
    } catch (e) {
      if (e.response?.status === 404) { toast.error("That account no longer exists — refreshing."); await refreshAll(); }
      else toast.error(formatApiError(e.response?.data?.detail));
    }
    finally { setBusyId(null); }
  };

  const remove = async (id) => {
    if (!window.confirm("Dekonekte kont sa a? L ap retire nan platfòm nan epi bot la p ap trade sou li ankò. Ou ka rekonekte l pita.")) return;
    setBusyId(id);
    try {
      await api.delete(`/accounts/${id}`);
      toast.success("Kont dekonekte");
      await refreshAll();
    } catch (e) {
      if (e.response?.status === 404) { toast.success("Kont dekonekte"); await refreshAll(); }
      else toast.error(formatApiError(e.response?.data?.detail));
    }
    finally { setBusyId(null); }
  };

  const syncAccount = async (id) => {
    setBusyId(id);
    const t = toast.loading("Refreshing connection…");
    try {
      const { data } = await api.post(`/accounts/${id}/sync`);
      await refreshAll();
      await fetchLive();
      if (data.status === "connected") toast.success("Connected — balance updated", { id: t });
      else toast.error(data.sync_message || "Still disconnected. Check login/server.", { id: t });
    } catch (e) {
      if (e.response?.status === 404) { toast.error("That account no longer exists — refreshing.", { id: t }); await refreshAll(); }
      else toast.error(formatApiError(e.response?.data?.detail) || "Refresh failed", { id: t });
    }
    finally { setBusyId(null); }
  };

  const save = async () => {
    setBusy(true);
    try {
      const payload = tab === "mt5"
        ? { type: "mt5", broker: mt5.broker || "MetaTrader 5", account_id: mt5.account_id, login: mt5.account_id, password: mt5.password, server: mt5.server, platform: "MT5", balance: 0 }
        : { type: "propfirm", firm_name: prop.firm_name, challenge_phase: prop.challenge_phase, account_id: prop.account_id, login: prop.account_id, password: prop.password, server: prop.server, platform: "MT5", balance: Number(prop.balance) || 100000, initial_balance: Number(prop.balance) || 100000, objectives: prop.objectives };
      await api.post("/account", payload);
      toast.success("MT5 account connected");
      setOpen(false);
      await refreshAll();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Failed to connect account");
    } finally {
      setBusy(false);
    }
  };

  const dialogProps = { open, setOpen, tab, setTab, mt5, setMt5, prop, setProp, busy, save,
    propfirmLocked, phases: entitlements?.propfirm_phases };

  const goConnect = () => navigate("/app/account/connect");
  const addBtn = (
    <Button data-testid="add-account-button" disabled={!canAddMore} onClick={goConnect}
      className="h-11 rounded-full px-6 text-white font-semibold hover:opacity-90 disabled:opacity-40" style={{ backgroundColor: "var(--sfx-accent)" }}>
      <Plus className="mr-2 h-4 w-4" /> Connect Account
    </Button>
  );

  return (
    <div className="px-5 pt-6 pb-6 fade-up">
      <div className="flex items-center justify-between mb-2">
        <div>
          <h1 className="font-heading text-2xl font-bold tracking-tight">MetaTrader Accounts</h1>
          <p className="text-[#5f7ba6] text-sm mt-1">
            {list.length}/{maxAccounts === 999 ? "∞" : maxAccounts} account{maxAccounts === 1 ? "" : "s"} · {entitlements?.plan_name || "No plan"}
          </p>
        </div>
        {list.length > 0 && canAddMore && (
          <Button data-testid="change-account-button" onClick={goConnect} variant="outline"
            className="h-10 rounded-full border-[#24406b] bg-transparent text-white hover:bg-[#0f1a2e]">
            <Plus className="mr-2 h-4 w-4" /> Connect
          </Button>
        )}
      </div>

      {!canAddMore && list.length >= maxAccounts && maxAccounts > 0 && (
        <div className="mb-4 flex items-center gap-2 text-xs text-[#8fb4e6] border border-[#14233d] bg-[#0A1120] rounded-xl px-4 py-2.5" data-testid="account-limit-note">
          <Lock className="h-3.5 w-3.5" /> Your {entitlements?.plan_name} plan allows {maxAccounts} MT5 account{maxAccounts === 1 ? "" : "s"}. Upgrade for more.
        </div>
      )}

      {list.length === 0 ? (
        <div className="border border-[#14233d] bg-[#0A1120] rounded-3xl p-10 text-center">
          <div className="h-16 w-16 rounded-full mx-auto mb-4 flex items-center justify-center" style={{ backgroundColor: "var(--sfx-accent-soft)" }}>
            <Server className="h-7 w-7" style={{ color: "var(--sfx-accent)" }} />
          </div>
          <h3 className="font-heading text-lg font-bold mb-1">No account connected</h3>
          <p className="text-sm text-[#5f7ba6] mb-6">Add your MetaTrader 5 account to start AutoTrade.</p>
          {addBtn}
        </div>
      ) : (
        <div className="space-y-4">
          {activeAcc && live?.live && (
            <div className="border rounded-3xl overflow-hidden" style={{ borderColor: "var(--sfx-accent)" }} data-testid="account-live-metrics">
              <div className="px-5 py-2.5 bg-[#0A1120] border-b border-[#14233d] font-mono-term text-[10px] uppercase tracking-widest text-[#22e07b] flex items-center gap-1.5">
                <Wifi className="h-3 w-3" /> Live · MetaAPI · {activeAcc.broker || activeAcc.firm_name}
              </div>
              <div className="grid grid-cols-2 gap-px bg-[#14233d]">
                <LiveMetric label="Balance" value={fmtMoney(live.balance, live.currency)} />
                <LiveMetric label="Equity" value={fmtMoney(live.equity, live.currency)} tone={live.equity >= live.balance ? "up" : "down"} />
                <LiveMetric label="Free Margin" value={fmtMoney(live.free_margin, live.currency)} />
                <LiveMetric label="Leverage" value={live.leverage ? `1:${live.leverage}` : "—"} />
              </div>
            </div>
          )}

          {(() => {
            const mt5List = list.filter((a) => a.type !== "propfirm");
            const propList = list.filter((a) => a.type === "propfirm");
            const Section = ({ title, icon, items }) => (
              items.length === 0 ? null : (
                <div className="space-y-3" data-testid={`account-group-${title.replace(/\s+/g, "-").toLowerCase()}`}>
                  <div className="flex items-center gap-2 px-1">
                    {icon}
                    <h2 className="font-mono-term text-[11px] uppercase tracking-widest text-[#5f7ba6]">{title}</h2>
                    <span className="text-[10px] text-[#5f7ba6]">({items.length})</span>
                  </div>
                  {items.map((acc) => (
                    <AccountCard key={acc.id} acc={acc} isActive={acc.id === activeAcc?.id}
                      onActivate={activate} onRemove={remove} onSync={syncAccount}
                      busyId={busyId} canRemove={true} />
                  ))}
                </div>
              )
            );
            return (
              <div className="space-y-6">
                <Section title="MT5 Broker Accounts" icon={<Server className="h-4 w-4 text-[#8fb4e6]" />} items={mt5List} />
                <Section title="Prop Firm Accounts" icon={<Landmark className="h-4 w-4 text-[#f5a623]" />} items={propList} />
              </div>
            );
          })()}

          {live?.live && live.positions?.length > 0 && (
            <div className="border border-[#14233d] bg-[#0A1120] rounded-3xl overflow-hidden" data-testid="account-positions-card">
              <div className="px-5 py-3 border-b border-[#14233d] font-mono-term text-xs uppercase tracking-widest text-[#5f7ba6]">
                Open Positions ({live.positions.length})
              </div>
              {live.positions.map((p) => (
                <div key={p.id} data-testid={`position-${p.id}`} className="flex items-center justify-between px-5 py-3.5 border-b border-[#14233d]">
                  <div>
                    <div className="font-heading font-semibold text-sm">{p.symbol} <span className={`ml-1 text-xs ${p.type?.includes("BUY") ? "text-[#22e07b]" : "text-[#ff5b5b]"}`}>{p.type?.includes("BUY") ? "BUY" : "SELL"}</span></div>
                    <div className="font-mono-term text-xs text-[#5f7ba6]">{p.volume} lots @ {p.open_price}</div>
                  </div>
                  <div className={`font-heading font-bold ${p.profit >= 0 ? "text-[#22e07b]" : "text-[#ff5b5b]"}`}>
                    {p.profit >= 0 ? "+" : ""}{fmtMoney(p.profit, live.currency)}
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
}
